import React, { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { Box, Typography, Chip } from "@mui/material";

// Fix default marker icons for leaflet + webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Color palette for multiple routes
const ROUTE_COLORS = [
  "#0369A1", "#059669", "#D97706", "#DC2626", "#7C3AED",
  "#DB2777", "#0891B2", "#65A30D", "#EA580C", "#4F46E5",
];

// Create numbered marker icon
const createNumberedIcon = (number, color = "#0369A1") => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background: ${color};
      color: white;
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 12px;
      font-family: Inter, sans-serif;
      border: 2.5px solid white;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    ">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -16],
  });
};

// Create starting point icon
const createStartIcon = (color = "#059669") => {
  return L.divIcon({
    className: "custom-marker-start",
    html: `<div style="
      background: ${color};
      color: white;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 14px;
      font-family: Inter, sans-serif;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.35);
    ">S</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -18],
  });
};

/**
 * RouteMap - Displays routes on an interactive map
 *
 * Props:
 *   routes: Array of route objects, each with:
 *     - name: string
 *     - orders: Array of { patient_name, address, lat/latitude, lng/longitude }
 *     - starting_point: { lat, lng, name } (optional)
 *     - medical_professional_name: string (optional)
 *   height: string/number (default "400px")
 *   showLegend: boolean (default true)
 *   singleRouteColor: string (override color for single route)
 */
const RouteMap = ({
  routes = [],
  height = "400px",
  showLegend = true,
  singleRouteColor,
}) => {
  // Extract all valid coordinates
  const { markers, polylines, bounds } = useMemo(() => {
    const allMarkers = [];
    const allPolylines = [];
    const allCoords = [];

    routes.forEach((route, routeIndex) => {
      const color = singleRouteColor || ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];
      const routeCoords = [];

      // Starting point
      if (route.starting_point?.lat && route.starting_point?.lng) {
        const sp = route.starting_point;
        allMarkers.push({
          key: `start-${routeIndex}`,
          position: [sp.lat, sp.lng],
          icon: createStartIcon(color),
          popup: `Start: ${sp.name || sp.address || "Starting Point"}`,
          isStart: true,
        });
        routeCoords.push([sp.lat, sp.lng]);
        allCoords.push([sp.lat, sp.lng]);
      }

      // Order stops
      const orders = route.orders || [];
      orders.forEach((order, orderIndex) => {
        const lat = order.coordinates?.lat || order.lat || order.latitude || order.patient?.latitude;
        const lng = order.coordinates?.lng || order.lng || order.longitude || order.patient?.longitude;

        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          const patientName = order.patient_name || order.patient?.first_name
            ? `${order.patient?.first_name || ""} ${order.patient?.last_name || ""}`.trim()
            : `Stop ${orderIndex + 1}`;
          const address = order.address || order.patient_address ||
            (order.patient ? `${order.patient.address || ""}, ${order.patient.city || ""}` : "");

          allMarkers.push({
            key: `stop-${routeIndex}-${orderIndex}`,
            position: [lat, lng],
            icon: createNumberedIcon(orderIndex + 1, color),
            popup: `#${orderIndex + 1}: ${patientName}${address ? `\n${address}` : ""}`,
            patientName,
            address,
            stopNumber: orderIndex + 1,
          });
          routeCoords.push([lat, lng]);
          allCoords.push([lat, lng]);
        }
      });

      // Create polyline for this route
      if (routeCoords.length >= 2) {
        allPolylines.push({
          key: `line-${routeIndex}`,
          positions: routeCoords,
          color: color,
          name: route.name || `Route ${routeIndex + 1}`,
        });
      }
    });

    return { markers: allMarkers, polylines: allPolylines, bounds: allCoords };
  }, [routes, singleRouteColor]);

  if (bounds.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#F1F5F9",
          borderRadius: 3,
          border: "1px solid #E2E8F0",
        }}
      >
        <Typography color="text.secondary" variant="body2">
          No location data available for map display
        </Typography>
      </Box>
    );
  }

  // Calculate center from bounds
  const centerLat = bounds.reduce((s, c) => s + c[0], 0) / bounds.length;
  const centerLng = bounds.reduce((s, c) => s + c[1], 0) / bounds.length;

  return (
    <Box sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #E2E8F0" }}>
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={11}
        style={{ height, width: "100%" }}
        bounds={bounds.length >= 2 ? bounds : undefined}
        boundsOptions={{ padding: [40, 40] }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Route polylines */}
        {polylines.map((line) => (
          <Polyline
            key={line.key}
            positions={line.positions}
            color={line.color}
            weight={3}
            opacity={0.7}
            dashArray="8 4"
          />
        ))}

        {/* Markers */}
        {markers.map((marker) => (
          <Marker key={marker.key} position={marker.position} icon={marker.icon}>
            <Popup>
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: "13px" }}>
                {marker.isStart ? (
                  <strong>{marker.popup}</strong>
                ) : (
                  <>
                    <strong>Stop {marker.stopNumber}: {marker.patientName}</strong>
                    {marker.address && (
                      <div style={{ color: "#64748B", marginTop: 4 }}>{marker.address}</div>
                    )}
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Legend */}
      {showLegend && routes.length > 1 && (
        <Box sx={{ p: 1.5, bgcolor: "#FAFBFC", display: "flex", flexWrap: "wrap", gap: 1, borderTop: "1px solid #F1F5F9" }}>
          {routes.map((route, i) => (
            <Chip
              key={i}
              label={route.name || route.medical_professional_name || `Route ${i + 1}`}
              size="small"
              sx={{
                bgcolor: ROUTE_COLORS[i % ROUTE_COLORS.length] + "15",
                color: ROUTE_COLORS[i % ROUTE_COLORS.length],
                fontWeight: 600,
                fontSize: "0.75rem",
                borderLeft: `3px solid ${ROUTE_COLORS[i % ROUTE_COLORS.length]}`,
                borderRadius: "6px",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RouteMap;
